/**
 * JPL Horizons bodies, ported from loader-horizons.js: the $$SOE vector-table
 * parser and the per-body fetch (24 h window from now, 10 min step) through
 * loaderApi.horizons. The runtime pushes each body in the store's
 * horizonsBodies list through engine.setHorizonsBody and removes it on
 * toggle off. HORIZONS_BODIES (constants.ts) maps key to Horizons id and label.
 */
import { HORIZONS_BODIES } from '../constants';
import { loaderApi } from '../data/api';
import type { EphemerisSample } from '../types';
import { useAwareness, type HorizonsBodyState } from './store';

export const HORIZONS_WINDOW_MS = 24 * 3_600_000;
export const HORIZONS_STEP = '10 min';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DATE_LINE = /A\.D\.\s+(\d{4})-(\w{3})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/;
const POSITION_LINE = /X\s*=\s*([-+\d.E]+)\s+Y\s*=\s*([-+\d.E]+)\s+Z\s*=\s*([-+\d.E]+)/;
const VELOCITY_LINE = /VX\s*=\s*([-+\d.E]+)\s+VY\s*=\s*([-+\d.E]+)\s+VZ\s*=\s*([-+\d.E]+)/;

/** Horizons wants 'YYYY-MM-DD HH:MM:SS' (the proxy quotes it). */
export function formatHorizonsTime(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').split('.')[0];
}

/**
 * Samples from a Horizons VECTORS response (VEC_TABLE 2, km and km/s, J2000
 * Earth-centred): the $$SOE to $$EOE block holds one date line per record
 * followed by the X Y Z line and the VX VY VZ line. Every record is read (the
 * original loader-horizons.js skipped every second one). Throws when the
 * markers are missing (the first 120 characters carry the diagnostic) or no
 * record parses. The proxy explicitly requests UT timestamps (not default TDB).
 */
export function parseHorizonsVectors(text: string): EphemerisSample[] {
  if (!text || typeof text !== 'string') throw new Error('Empty Horizons response');
  const soe = text.indexOf('$$SOE');
  const eoe = text.indexOf('$$EOE');
  if (soe < 0 || eoe < 0) {
    throw new Error('Horizons response missing $$SOE/$$EOE: ' + text.substring(0, 120).replace(/\s+/g, ' '));
  }
  const lines = text
    .substring(soe + 5, eoe)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const samples: EphemerisSample[] = [];
  for (let i = 0; i < lines.length; i++) {
    const date = DATE_LINE.exec(lines[i]);
    if (!date) continue;
    const month = MONTHS.indexOf(date[2]);
    if (month < 0 || i + 1 >= lines.length) continue;
    const pos = POSITION_LINE.exec(lines[i + 1]);
    if (!pos) continue;
    const vel = i + 2 < lines.length ? VELOCITY_LINE.exec(lines[i + 2]) : null;
    const t = `${date[1]}-${String(month + 1).padStart(2, '0')}-${date[3]}T${date[4]}:${date[5]}:${date[6]}Z`;
    const sample: EphemerisSample = { t, x: parseFloat(pos[1]), y: parseFloat(pos[2]), z: parseFloat(pos[3]) };
    if (vel) {
      sample.vx = parseFloat(vel[1]);
      sample.vy = parseFloat(vel[2]);
      sample.vz = parseFloat(vel[3]);
      i += 2;
    } else {
      i += 1;
    }
    samples.push(sample);
  }
  if (samples.length === 0) throw new Error('Horizons response had $$SOE/$$EOE but no parseable samples');
  return samples;
}

/** Vectors for a Horizons command over [startMs, stopMs] at the given step, through the Worker proxy. */
export async function fetchHorizonsVectors(
  command: string,
  startMs: number,
  stopMs: number,
  step: string,
  fetchText: (command: string, startIso: string, stopIso: string, step: string) => Promise<string> = loaderApi.horizons,
  formatTime: (ms: number) => string = formatHorizonsTime,
): Promise<EphemerisSample[]> {
  const text = await fetchText(command, formatTime(startMs), formatTime(stopMs), step);
  return parseHorizonsVectors(text);
}

export function horizonsBody(key: string): { key: string; id: string; label: string } | undefined {
  return HORIZONS_BODIES.find((b) => b.key === key);
}

/** Fetch one body's 24 h ephemeris from now and store it; throws when the key is unknown or the fetch fails. */
export async function loadHorizonsBody(key: string, nowMs: number = Date.now()): Promise<HorizonsBodyState> {
  const spec = horizonsBody(key);
  if (!spec) throw new Error(`Unknown Horizons body: ${key}`);
  const samples = await fetchHorizonsVectors(spec.id, nowMs, nowMs + HORIZONS_WINDOW_MS, HORIZONS_STEP);
  const body: HorizonsBodyState = { label: spec.label, samples, fetchedMs: nowMs };
  useAwareness.getState().setHorizonsBody(key, body);
  return body;
}
